import * as Icon from "@iyk/icons"
import * as ReactDropdownMenu from "@radix-ui/react-dropdown-menu"

import { Avatar } from "./avatar.tsx"
import { cx } from "./cva.ts"

const { Root, Trigger, Portal, Group } = ReactDropdownMenu

const Content = ({
  sideOffset = 4,
  align = "start",
  className,
  ...props
}: Dropdown.Content.Props) => {
  return (
    <ReactDropdownMenu.Content
      sideOffset={sideOffset}
      align={align}
      className={cx(
        "z-50 overflow-hidden",
        "flex flex-col gap-0.5",
        "bg-background dark:bg-gray-3",
        "min-w-64 rounded p-1.5 shadow-lg",
        "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=closed]:blur-[1px]",
        className,
      )}
      {...props}
    />
  )
}

const Separator = ({ className, ...props }: Dropdown.Separator.Props) => {
  return (
    <ReactDropdownMenu.Separator className={cx("h-px bg-gray-4 my-0.5", className)} {...props} />
  )
}

const Item = ({ className, ...props }: Dropdown.Item.Props) => {
  return <ReactDropdownMenu.Item className={cx(classForItem, "px-3", className)} {...props} />
}
const classForItem = cx(
  "cursor-pointer",
  "text-sm-title rounded-sm",
  "h-9",
  "flex items-center gap-2",
  "hover:bg-gray-3 focus:bg-gray-3",
  "dark:hover:bg-gray-5 dark:focus:bg-gray-5",
  "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
  "relative select-none outline-none",
)

const AvatarItem = ({ className, children, src, variant, ...props }: Dropdown.AvatarItem.Props) => {
  return (
    <ReactDropdownMenu.Item className={cx(classForItem, "px-1", className)} {...props}>
      <Avatar className="size-7" src={src} variant={variant} />
      {children}
    </ReactDropdownMenu.Item>
  )
}

const RadioGroup = ({ className, indicatorSide, ...props }: Dropdown.RadioGroup.Props) => {
  return (
    <ReactDropdownMenu.RadioGroup
      data-indicator-side={indicatorSide}
      className={cx(className, "group")}
      {...props}
    />
  )
}

const RadioItem = ({ className, children, ...props }: Dropdown.RadioItem.Props) => {
  return (
    <ReactDropdownMenu.RadioItem className={cx(classForRadioItem, "px-3", className)} {...props}>
      <ReactDropdownMenu.ItemIndicator className={classForItemIndicator}>
        <Icon.Check className="size-4" />
      </ReactDropdownMenu.ItemIndicator>
      <span className="grow flex items-center gap-2">{children}</span>
    </ReactDropdownMenu.RadioItem>
  )
}
const classForRadioItem = cx(
  classForItem,

  // indicatorSide = left
  "group-data-[indicator-side=left]:pl-8",
  "group-data-[indicator-side=left]:data-[state=checked]:!pl-0",
  // indicatorSide = right
  "group-data-[indicator-side=right]:pr-8 group-data-[indicator-side=right]:flex-row-reverse",
  "group-data-[indicator-side=right]:data-[state=checked]:!pr-0",
)
const classForItemIndicator = cx(
  "flex items-center justify-center",
  "group-data-[indicator-side=left]:pl-2",
  "group-data-[indicator-side=right]:pr-2",
)

const RadioAvatarItem = ({
  className,
  children,
  src,
  variant,
  ...props
}: Dropdown.RadioAvatarItem.Props) => {
  return (
    <ReactDropdownMenu.RadioItem className={cx(classForRadioItem, "px-1", className)} {...props}>
      <ReactDropdownMenu.ItemIndicator className={classForItemIndicator}>
        <Icon.Check className="size-4" />
      </ReactDropdownMenu.ItemIndicator>
      <span className="grow flex items-center gap-2">
        <Avatar className="size-5" src={src} variant={variant} />
        {children}
      </span>
    </ReactDropdownMenu.RadioItem>
  )
}

export namespace Dropdown {
  export namespace Content {
    export type Props = React.ComponentProps<typeof ReactDropdownMenu.Content>
  }

  export namespace Item {
    export type Props = React.ComponentProps<typeof ReactDropdownMenu.Item>
  }

  export namespace AvatarItem {
    export type Props = React.ComponentProps<typeof ReactDropdownMenu.Item> & {
      src: Avatar.Props["src"]
      variant: Avatar.Props["variant"]
    }
  }

  export namespace Separator {
    export type Props = React.ComponentProps<typeof ReactDropdownMenu.Separator>
  }

  export namespace RadioGroup {
    export type Props = React.ComponentProps<typeof ReactDropdownMenu.RadioGroup> & {
      indicatorSide: "left" | "right"
    }
  }

  export namespace RadioItem {
    export type Props = React.ComponentProps<typeof ReactDropdownMenu.RadioItem>
  }

  export namespace RadioAvatarItem {
    export type Props = React.ComponentProps<typeof ReactDropdownMenu.RadioItem> & {
      src: Avatar.Props["src"]
      variant: Avatar.Props["variant"]
    }
  }
}

export const Dropdown = {
  Content,
  Group,
  Item,
  AvatarItem,
  Portal,
  Root,
  Separator,
  Trigger,
  RadioGroup,
  RadioItem,
  RadioAvatarItem,
}
